/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

export const DEFAULT_ALLSTAR_DOMAINS = 'airasia.com,movetravel.com';
export const parseAllStarDomains = (raw: string): string[] =>
  raw
    .replace(/[[\]"']/g, ' ')
    .split(/[,\s]+/)
    .map((domain) => domain.trim().toLowerCase().replace(/^@/, ''))
    .filter(Boolean);

/**
 * Whether `email` belongs to one of the configured internal domains.
 *
 * Matches the host after the last `@` either exactly (`airasia.com`) or as a
 * sub-domain (`corp.airasia.com`). Substring matching is deliberately avoided
 * so `user@airasia.com.attacker.net` does not qualify.
 *
 * @param email   Account email / username. `undefined` returns `false`.
 */
export const isAllStarEmail = (
  email: string | undefined,
  domains: string | undefined = DEFAULT_ALLSTAR_DOMAINS,
): boolean => {
  const normalised = email?.trim().toLowerCase();
  if (!normalised) return false;

  const atIndex = normalised.lastIndexOf('@');
  if (atIndex === -1) return false;
  const host = normalised.slice(atIndex + 1);
  if (!host) return false;

  const allowList = parseAllStarDomains(domains || DEFAULT_ALLSTAR_DOMAINS);
  return allowList.some(
    (domain) => host === domain || host.endsWith(`.${domain}`),
  );
};
